import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from "@mui/material";
import { useCallback } from "react";
import { Flex } from "~/components/Flex";
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  TimerOutlined as TimerOutlinedIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Text, TextCaption } from "~/components/ui-library";
import { FormatDate } from "~/lib/date";
import {
  ExceptionMutationAction,
  ExceptionReviewAction,
  useLoadAssetQuery,
} from "~/operations";
import { usePendingReview } from "../hooks/usePendingReview";
import { SpaceScope } from "~/hooks/useScope";
import { LoadingFailedPage } from "~/components/loading";
import { Paginate } from "~/components/pagination/paginate";
import { Link as RouterLink } from "react-router-dom";
import { ListCountChip } from "./ListCountChip";
import ExceptionButtons from "./ExceptionButtons";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { ExceptionGroupTables } from "~/pages/findings/Exceptions/components/ExceptionGroupTables";
import { DisabledIcon, FalsePositiveIcon, WorkaroundIcon } from "~/icons";

interface PendingExceptionsModalProps {
  spaceScope: SpaceScope;
  onReviewActionChange?: (
    value: ExceptionGroup,
    action: ExceptionReviewAction,
  ) => void;
  isReviewLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  loadingExceptionId: string | undefined;
  totalCount?: number;
}

export function PendingExceptionsModal({
  spaceScope,
  onReviewActionChange,
  isReviewLoading = false,
  loadingAction,
  loadingExceptionId,
  totalCount = 0,
}: PendingExceptionsModalProps) {
  const spaceId = spaceScope.id;
  const pendingReviewExceptionData = usePendingReview({
    scopeMrn: spaceScope.mrn,
  });

  const exceptionGroup =
    pendingReviewExceptionData.pendingExceptionsGroups.at(0);

  const isAssetScope =
    exceptionGroup && exceptionGroup.scopeMrn.startsWith("//assets");
  const assetResult = useLoadAssetQuery({
    variables: { mrn: exceptionGroup?.scopeMrn || "" },
    skip: !isAssetScope,
  });
  const asset = assetResult.data?.asset;

  const scroll = "paper";
  const isThisExceptionLoading =
    isReviewLoading && loadingExceptionId === exceptionGroup?.id;

  const getDialogContent = useCallback(() => {
    if (!pendingReviewExceptionData.isOpen) return <></>;
    if (
      !exceptionGroup &&
      pendingReviewExceptionData.pendingExceptions.loading
    ) {
      return (
        <Stack gap={2}>
          <Skeleton variant="rectangular" height={62} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      );
    }
    if (!exceptionGroup) {
      return <LoadingFailedPage />;
    }

    const isRiskAccepted =
      exceptionGroup.action === ExceptionMutationAction.RiskAccepted;
    const isDisabled =
      exceptionGroup.action === ExceptionMutationAction.Disable;
    const isWorkaround =
      exceptionGroup.action === ExceptionMutationAction.Workaround;
    const isFalsePositive =
      exceptionGroup.action === ExceptionMutationAction.FalsePositive;

    return (
      <Flex flexDirection="column" flex={1} gap={2}>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column" gap={1}>
            <Flex justifyContent="space-between">
              <Flex center gap={2}>
                {isRiskAccepted && <ThumbUpOffAltIcon />}
                {isDisabled && <DisabledIcon />}
                {isWorkaround && <WorkaroundIcon />}
                {isFalsePositive && <FalsePositiveIcon />}
                <Text bold fontSize={24}>
                  {exceptionGroup.title}
                </Text>
              </Flex>
            </Flex>
            <Flex justifyContent="space-between">
              <Flex gap={2}>
                <Flex center gap={1}>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 12, color: "text.secondary" }}
                  />
                  <TextCaption bold secondary>
                    {exceptionGroup.exceptionId}
                  </TextCaption>
                </Flex>
                {exceptionGroup.author && (
                  <Tooltip
                    title={`Created by: ${exceptionGroup.author.name}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <PersonOutlineIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {exceptionGroup.author.name}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                )}
                <Tooltip
                  title={`Created: ${FormatDate(exceptionGroup.createdAt)}`}
                  arrow
                  placement="top"
                >
                  <Flex center gap={1}>
                    <AccessTimeIcon
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <TextCaption bold secondary>
                      {FormatDate(exceptionGroup.createdAt)}
                    </TextCaption>
                  </Flex>
                </Tooltip>
                {exceptionGroup.validUntil && (
                  <Tooltip
                    title={`Valid Until: ${FormatDate(exceptionGroup.validUntil)}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <TimerOutlinedIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {FormatDate(exceptionGroup.validUntil)}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={1.5} alignItems="flex-end">
            <ExceptionButtons
              exceptionGroup={exceptionGroup}
              onReviewActionChange={onReviewActionChange}
              isLoading={isThisExceptionLoading}
              loadingAction={loadingAction}
            />
            {exceptionGroup.reviewer && (
              <Flex gap={2}>
                <Tooltip
                  title={`Reviewed by: ${exceptionGroup.reviewer.name}`}
                  arrow
                  placement="top"
                >
                  <Flex center gap={1}>
                    <VisibilityIcon
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <TextCaption bold secondary>
                      {exceptionGroup.reviewer.name}
                    </TextCaption>
                  </Flex>
                </Tooltip>
                <Tooltip
                  title={`Reviewed: ${FormatDate(exceptionGroup.modifiedAt)}`}
                  arrow
                  placement="top"
                >
                  <Flex center gap={1}>
                    <AccessTimeIcon
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <TextCaption bold secondary>
                      {FormatDate(exceptionGroup.modifiedAt)}
                    </TextCaption>
                  </Flex>
                </Tooltip>
              </Flex>
            )}
          </Flex>
        </Flex>

        {exceptionGroup.justification && (
          <Text secondary>{exceptionGroup.justification}</Text>
        )}

        <ExceptionGroupTables
          exceptionGroup={exceptionGroup}
          exceptionScope="space"
          assets={asset ? [asset] : []}
          showFindingsWhenSingle
        />
      </Flex>
    );
  }, [
    pendingReviewExceptionData.pendingExceptions.loading,
    exceptionGroup,
    asset,
    loadingAction,
    isReviewLoading,
    loadingExceptionId,
    onReviewActionChange,
    spaceId,
  ]);

  return (
    <Dialog
      open={pendingReviewExceptionData.isOpen}
      onClose={pendingReviewExceptionData.handleModalClose}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        id="scroll-dialog-title"
        sx={{
          fontSize: 24,
          fontWeight: 700,
          textTransform: "uppercase",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex center gap={1}>
          Exceptions Needing Approval <ListCountChip count={totalCount} />
        </Flex>
        <IconButton
          aria-label="close"
          component={RouterLink}
          to={pendingReviewExceptionData.closeUrl.href}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers={scroll === "paper"}
        sx={{
          flexGrow: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Flex flexDirection="column" flex={1}>
          {getDialogContent()}
        </Flex>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Paginate
          {...pendingReviewExceptionData.paginate.pageProps(
            pendingReviewExceptionData.pendingExceptions.data
              ?.listExceptionGroups.pageInfo,
          )}
          variant="stepper"
        />
      </DialogActions>
    </Dialog>
  );
}
